import { select } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import { Button } from '@ps/web-shared-components';
import JSON5 from 'json5';
import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { CMSHeading } from '@swf/cms';
import HTMLText from '@swf/components/HTMLText/HTMLText';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import useTemplateContent from '@swf/core/hooks/useTemplateContent';
import { type Dispatch } from '@swf/core/store';
import type { PSNPartnerNotification } from '@swf/core/types/Notification';

import NotificationContainer from '../NotificationContainer/NotificationContainer';

import { TXTCLI_LoadingXX } from './PartnerNotification.messages.json';
import styles from './PartnerNotification.scss';

const PartnerNotification: React.FC = () => {
  const { formatMessage } = useIntl();
  const isEmebedded = useSelector(select.device.isEmbedded);
  const searchParams = useSearchParams<PSNPartnerNotification>();
  const windowPayloadParams =
    isEmebedded && canUseDOM && (window.starsweb?.payload as PSNPartnerNotification);
  if (windowPayloadParams && typeof windowPayloadParams.templateContext === 'string') {
    windowPayloadParams.templateContext = JSON5.parse(windowPayloadParams.templateContext);
  }
  const notification = windowPayloadParams || searchParams;
  const { content, title, actions, loading, error } = useTemplateContent(notification);
  const { modal, notification: notificationDispatch } = useDispatch<Dispatch>();
  const psnPartnerNotificationChannel = useRef(
    new BroadcastChannel('psn-partner-notification-modal')
  );

  useEffect(() => {
    if (isEmebedded) {
      notificationDispatch.addPSNPartnerNotification(notification);
    }
  }, [isEmebedded, notification, notificationDispatch]);

  const handleClose = async () => {
    await notificationDispatch.aknowledgePSNPartnerNotification(notification);
    if (isEmebedded) {
      window.close();
    } else {
      modal.unqueuePSNPartnerNotification({ path: '/notifications/psn-partner', notification });
      psnPartnerNotificationChannel.current.postMessage(notification);
    }
  };

  if (loading)
    return (
      <NotificationContainer hideCloseButton>
        {formatMessage(TXTCLI_LoadingXX)}
      </NotificationContainer>
    );
  if (error)
    return (
      <NotificationContainer styledNotification>
        {(error as Error).message || error.toString()}
      </NotificationContainer>
    );

  return (
    <NotificationContainer title={title} onClose={handleClose} hideCloseButton>
      {isEmebedded && <CMSHeading level="h1" title={title?.trim()} alignment="left" hero={false} />}
      <HTMLText className={styles.content} html={content?.trim()} />
      <div className={styles.buttonContainer}>
        {actions?.map((action) => (
          <Button className={styles.button} onClick={handleClose} key={action.id}>
            {action.label.trim()}
          </Button>
        ))}
      </div>
    </NotificationContainer>
  );
};

export default PartnerNotification;
